import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Locale-aware formatting helpers live in '@/lib/formatters' (issue #337).
// Re-exported here for backward compatibility with existing imports.
export {
  getIntlLocale,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatLocalDate,
  CURRENCY_SYMBOL,
} from './formatters';

/**
 * Sanitize email HTML content for display.
 * Strips document structure (DOCTYPE, html, head, body tags) and extracts body content.
 * Also handles cases where HTML appears as plain text within the content.
 */
/**
 * Compute the SHA-256 address hash used by the coverage checks system.
 *
 * Mirrors `CoverageCheck::addressHashFor()` backend normalisation:
 * sha256 of pipe-delimited, lowercased, trimmed components:
 *   street|postalCode|city|country
 *
 * Returns null when the address is incomplete (missing street or city).
 */
export async function computeAddressHash(address: {
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
}): Promise<string | null> {
  const street = address.street?.trim();
  const city = address.city?.trim();
  if (!street || !city) return null;

  const normalised = [
    street.toLowerCase(),
    (address.postal_code ?? '').toLowerCase().trim(),
    city.toLowerCase(),
    (address.country ?? '').toLowerCase().trim(),
  ].join('|');

  const encoder = new TextEncoder();
  const data = encoder.encode(normalised);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Format a service address object into a single-line display string.
 */
export function formatServiceAddress(address: {
  street?: string | null;
  street_2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  province?: string | null;
  country?: string | null;
} | null | undefined): string {
  if (!address) return '';
  const parts = [
    address.street,
    address.street_2,
    [address.city, address.postal_code].filter(Boolean).join(' '),
  ].filter(Boolean);
  return parts.join(', ');
}

/**
 * Sanitize inbound email HTML for safe rendering via `dangerouslySetInnerHTML`.
 *
 * SECURITY (H-1): inbound email bodies are fully untrusted (anyone can email a
 * support alias). The previous implementation was a regex allow/deny list that
 * did NOT strip `on*` event handlers, `javascript:`/`data:` URLs, `<iframe>`,
 * `<svg onload>`, etc. — a stored-XSS vector into the admin origin.
 *
 * This is now a DOM-based ALLOWLIST sanitizer:
 *  - parse the HTML in an inert document (never attached to the live DOM),
 *  - drop dangerous elements entirely (script/style/iframe/object/svg/…),
 *  - unwrap unknown elements (keep their text, drop the tag),
 *  - on allowed elements, strip every attribute except a small allowlist and
 *    reject any `href`/`src` whose scheme is not http(s)/mailto/tel.
 *
 * Encoded markup in text nodes (e.g. `&lt;script&gt;`) stays inert text.
 */

// Elements removed together with their contents — never safe to render.
const DANGEROUS_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'link',
  'meta',
  'base',
  'title',
  'head',
  'noscript',
  'template',
  'applet',
  'frame',
  'frameset',
]);

// Elements whose tag we keep. Anything not here and not dangerous is unwrapped.
const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'sub',
  'sup',
  'p',
  'br',
  'hr',
  'span',
  'div',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'blockquote',
  'pre',
  'code',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'colgroup',
  'col',
  'img',
]);

// Per-tag attribute allowlist ('*' applies to every allowed tag).
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  '*': new Set(['title']),
  a: new Set(['href', 'title', 'target']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan']),
  col: new Set(['span']),
  colgroup: new Set(['span']),
};

// Only these URL schemes are permitted in href/src. Relative URLs (no scheme)
// are also allowed. Anything else (javascript:, data:, vbscript:, file:, cid:…)
// is rejected.
const SAFE_URL_SCHEME = /^(https?|mailto|tel):/i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

function isSafeUrl(value: string): boolean {
  const trimmed = Array.from(value)
    .filter((c) => c.charCodeAt(0) > 0x20)
    .join('');
  if (trimmed === '') return false;
  // Scheme-relative (//host) is treated as absolute http(s) — allow.
  if (trimmed.startsWith('//')) return true;
  // No scheme at all → relative URL / anchor → safe.
  if (!HAS_SCHEME.test(trimmed)) return true;
  return SAFE_URL_SCHEME.test(trimmed);
}

function unwrapElement(el: Element): void {
  const parent = el.parentNode;
  if (!parent) {
    el.remove();
    return;
  }
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
}

function cleanSingleElement(el: Element): void {
  const tag = el.tagName.toLowerCase();

  if (DANGEROUS_TAGS.has(tag)) {
    el.remove();
    return;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    unwrapElement(el);
    return;
  }

  const tagAllowed = ALLOWED_ATTRS[tag];
  const globalAllowed = ALLOWED_ATTRS['*'];

  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const value = attr.value;

    // Drop all event handlers, inline styles and any non-allowlisted attribute.
    if (
      name.startsWith('on') ||
      name === 'style' ||
      name === 'srcset' ||
      name === 'xlink:href' ||
      (!globalAllowed.has(name) && !(tagAllowed?.has(name) ?? false))
    ) {
      el.removeAttribute(attr.name);
      continue;
    }

    if ((name === 'href' || name === 'src') && !isSafeUrl(value)) {
      el.removeAttribute(attr.name);
    }
  }

  // Harden links: force noopener/noreferrer and stop them opening in-app.
  if (tag === 'a' && el.hasAttribute('href')) {
    el.setAttribute('rel', 'noopener noreferrer nofollow');
    el.setAttribute('target', '_blank');
  }
}

/**
 * Conservative parser-work limits for untrusted email bodies. Real email
 * markup is shallow; these ceilings allow large newsletters while rejecting
 * adversarial inputs before DOMParser can consume excessive stack or memory.
 */
export const EMAIL_HTML_LIMITS = Object.freeze({
  maxInputLength: 1_000_000,
  maxTagCount: 10_000,
  maxNestingDepth: 256,
  maxTagLength: 16_384,
  maxFallbackLength: 200_000,
});

const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta',
  'param', 'source', 'track', 'wbr',
]);

export interface EmailHtmlComplexityInspection {
  accepted: boolean;
  inspectedCodeUnits: number;
}

/** @internal Exported so security tests can prove bounded scanner work. */
export function inspectEmailHtmlComplexity(html: string): EmailHtmlComplexityInspection {
  let inspectedCodeUnits = 0;
  const result = (accepted: boolean): EmailHtmlComplexityInspection => ({
    accepted,
    inspectedCodeUnits,
  });

  if (html.length > EMAIL_HTML_LIMITS.maxInputLength) return result(false);

  let tagCount = 0;
  const openTags: string[] = [];

  for (let index = 0; index < html.length; index += 1) {
    inspectedCodeUnits += 1;
    if (html[index] !== '<') continue;

    if (html.startsWith('<!--', index)) {
      let commentEnd = -1;
      for (let cursor = index + 4; cursor <= html.length - 3; cursor += 1) {
        inspectedCodeUnits += 1;
        if (html[cursor] === '-' && html[cursor + 1] === '-' && html[cursor + 2] === '>') {
          commentEnd = cursor;
          break;
        }
      }
      if (commentEnd === -1) return result(false);
      index = commentEnd + 2;
      continue;
    }

    let cursor = index + 1;
    let quote = '';
    for (; cursor < html.length; cursor += 1) {
      inspectedCodeUnits += 1;
      // maxTagLength includes both delimiters: `<` through `>` inclusive.
      if (cursor - index + 1 > EMAIL_HTML_LIMITS.maxTagLength) return result(false);

      const character = html[cursor];
      if (quote) {
        if (character === quote) quote = '';
      } else if (character === '"' || character === "'") {
        quote = character;
      } else if (character === '>') {
        break;
      }
    }

    if (cursor >= html.length) return result(false);

    const token = html.slice(index + 1, cursor).trim();
    index = cursor;
    if (!token || token[0] === '!' || token[0] === '?') continue;

    tagCount += 1;
    if (tagCount > EMAIL_HTML_LIMITS.maxTagCount) return result(false);

    const closing = token[0] === '/';
    const nameToken = closing ? token.slice(1).trimStart() : token;
    const tagName = nameToken.split(/[\s/>]/, 1)[0]?.toLowerCase() ?? '';
    if (!tagName) continue;

    if (closing) {
      // Only a close for the currently-open tag can reduce depth. Unmatched or
      // misnested closes are ignored conservatively so attacker-controlled
      // `</x>` tokens cannot hide subsequent nesting from the preflight.
      if (openTags[openTags.length - 1] === tagName) openTags.pop();
      continue;
    }

    // HTML ignores a self-closing slash on non-void elements (`<div/>` opens
    // a div). Only actual HTML void elements avoid the open-tag stack.
    if (VOID_TAGS.has(tagName)) continue;

    openTags.push(tagName);
    if (openTags.length > EMAIL_HTML_LIMITS.maxNestingDepth) return result(false);
  }

  return result(true);
}

function escapeAsControlledPlainText(html: string): string {
  let limited = html.slice(0, EMAIL_HTML_LIMITS.maxFallbackLength);
  const retainedLast = limited.charCodeAt(limited.length - 1);
  const sourceNext = html.charCodeAt(limited.length);
  if (
    retainedLast >= 0xd800 && retainedLast <= 0xdbff &&
    sourceNext >= 0xdc00 && sourceNext <= 0xdfff
  ) {
    limited = limited.slice(0, -1);
  }
  const suffix = html.length > limited.length ? '\n[Email content truncated for safe display]' : '';
  return `${limited}${suffix}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cleanElementsPostorder(roots: Element[]): void {
  const stack = roots.reverse().map((element) => ({ element, visited: false }));

  while (stack.length > 0) {
    const entry = stack.pop();
    if (!entry) continue;

    if (entry.visited) {
      cleanSingleElement(entry.element);
      continue;
    }

    stack.push({ element: entry.element, visited: true });
    const children = Array.from(entry.element.children);
    for (let index = children.length - 1; index >= 0; index -= 1) {
      stack.push({ element: children[index], visited: false });
    }
  }
}

export function sanitizeEmailHtml(html: string): string {
  if (!html) return '';

  // Run a linear, fail-closed guard before DOMParser. Inputs that exceed the
  // documented ceilings are displayed as escaped plain text (and bounded to a
  // safe fallback length), never partially parsed as executable markup.
  if (!inspectEmailHtmlComplexity(html).accepted) {
    return escapeAsControlledPlainText(html);
  }

  try {
    if (typeof DOMParser === 'undefined') return escapeAsControlledPlainText(html);

    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.body) return escapeAsControlledPlainText(html);

    // Clean each top-level element child. We must NOT pass `doc.body` itself to
    // the cleaner — `body` is not in the allowlist and would be unwrapped away.
    cleanElementsPostorder(Array.from(doc.body.children));

    return doc.body.innerHTML.trim();
  } catch {
    // Parser, traversal, and serialization failures all share one fail-closed
    // behavior. Never fall back to raw or regex-only HTML.
    return escapeAsControlledPlainText(html);
  }
}
